import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { getBanners } from "../../services/api/getAll";
import {
  addBanner,
  editBanner,
  deleteBanner,
} from "../../services/api/adminService";
import { useForm } from "react-hook-form";

const languages = ["en", "ar", "es"];

const BannersAdminScreen = () => {
  const [banners, setBanners] = useState([]);
  const [image, setImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { register, handleSubmit, reset, setValue } = useForm();

  const fetchData = async () => {
    try {
      const res = await getBanners();
      setBanners(res.items || []);
    } catch (error) {
      console.error("Failed to load banners", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (banner) => {
    setEditId(banner.id);
    setIsActive(banner.isActive);
    setImage(null);
    setExistingImageUrl(banner.imageUrl);
    banner.translations.forEach((t, index) => {
      setValue(`title_${t.languageCode}`, t.title);
      setValue(`description_${t.languageCode}`, t.description);
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteBanner(id);
      await fetchData();
    } catch (err) {
      console.error("Error deleting banner:", err);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const formData = new FormData();

      formData.append("id", editId || 0);
      formData.append("isActive", isActive.toString());

      if (image) {
        formData.append("image", image);
      }

      languages.forEach((lang, index) => {
        const title = data[`title_${lang}`];
        const description = data[`description_${lang}`];

        if (title || description) {
          formData.append(`translations[${index}].languageCode`, lang);
          if (title) {
            formData.append(`translations[${index}].title`, title);
          }
          if (description) {
            formData.append(`translations[${index}].description`, description);
          }
        }
      });

      if (editId) {
        await editBanner(formData);
      } else {
        await addBanner(formData);
      }

      reset();
      setImage(null);
      setIsActive(true);
      setEditId(null);
      setExistingImageUrl("");
      setOpen(false);
      await fetchData();
    } catch (err) {
      console.error("Error submitting banner:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    reset();
    setImage(null);
    setExistingImageUrl("");
    setIsActive(true);
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold" color="#000">
          Manage Banners
        </Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Add New Banner
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: 4,
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" color="#000" fontWeight="bold">
            {editId ? "Edit Banner" : "Add New Banner"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <form id="banner-form" onSubmit={handleSubmit(onSubmit)}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Button
                variant="outlined"
                component="label"
                sx={{ color: "#000", borderColor: "#ccc" }}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => setImage(e.target.files?.[0] || null)}
                />
              </Button>

              {(image || existingImageUrl) && (
                <Box>
                  <Typography fontSize={14} color="#666" mb={1}>
                    Preview:
                  </Typography>
                  <CardMedia
                    component="img"
                    height="180"
                    image={
                      image ? URL.createObjectURL(image) : existingImageUrl
                    }
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Active"
                sx={{ color: "#000" }}
              />

              {languages.map((lang) => (
                <Box key={lang} display="flex" flexDirection="column" gap={1}>
                  <TextField
                    label={`Title (${lang})`}
                    fullWidth
                    {...register(`title_${lang}`)}
                    InputLabelProps={{ style: { color: "#333" } }}
                    sx={{
                      input: { color: "#000" },
                      "& fieldset": { borderColor: "#ccc" },
                    }}
                  />
                  <TextField
                    label={`Description (${lang})`}
                    fullWidth
                    multiline
                    rows={2}
                    {...register(`description_${lang}`)}
                    InputLabelProps={{ style: { color: "#333" } }}
                    sx={{
                      textarea: { color: "#000" },
                      "& fieldset": { borderColor: "#ccc" },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} sx={{ color: "#333" }}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="banner-form"
            variant="contained"
            startIcon={
              loading && <CircularProgress size={18} color="inherit" />
            }
          >
            {loading ? "Saving..." : "Save Banner"}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2}>
        {banners.map((banner) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={banner.id}>
            <Card
              sx={{
                bgcolor: "#fefefe",
                borderRadius: 2,
                width: 300,
                mx: "auto",
              }}
            >
              <CardMedia
                component="img"
                height="180"
                image={banner.imageUrl}
                alt="Banner"
              />
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={banner.isActive ? "green" : "gray"}
                  >
                    {banner.isActive ? "Active" : "Inactive"}
                  </Typography>
                  <Box>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(banner)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(banner.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                {banner.translations.map((t) => (
                  <Box key={t.languageCode} mb={1}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="#000"
                    >
                      {t.languageCode.toUpperCase()}: {t.title}
                    </Typography>
                    <Typography variant="body2" color="#555">
                      {t.description}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default BannersAdminScreen;
